import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ExternalLink, Loader2 } from 'lucide-react'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { publicFetch } from '@/lib/publicFetch'
import type { ShareSnapshot } from '@/types/share'
import type { AtomicBreakdownEntry, Evidence, ScrutinyFlag } from '@/types/api'
import { ShareDocument } from './ShareDocument'
import { ShareRightPanel } from './ShareRightPanel'
import { buildSnapshotIndices } from './snapshotIndices'
import { renderMarkdownInline } from '@/lib/renderMarkdownInline'

const LANDING_URL = 'https://pharmatext.ai'

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; snapshot: ShareSnapshot }
  | { kind: 'notfound' }
  | { kind: 'expired' }
  | { kind: 'error'; message: string }

/**
 * Public, unauthenticated share page mounted at `/share/:token`.
 *
 * The layout mirrors the authenticated ``Workbench``: a two-column
 * shell with the read-only editor card on the left and a right panel
 * that toggles between Triage and Surgery. Everything renders from
 * the frozen ``snapshot_json`` returned by the public
 * ``GET /api/share/{token}`` endpoint — no follow-up API calls.
 */
export function ShareView() {
  const { token } = useParams<{ token: string }>()
  const [state, setState] = useState<LoadState>({ kind: 'loading' })

  useEffect(() => {
    if (!token) {
      /* eslint-disable-next-line react-hooks/set-state-in-effect -- gate transition from loading to notfound when route param is missing */
      setState({ kind: 'notfound' })
      return
    }
    let cancelled = false
    publicFetch(`/api/share/${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (cancelled) return
        if (res.status === 404) { setState({ kind: 'notfound' }); return }
        if (res.status === 410) { setState({ kind: 'expired' }); return }
        if (!res.ok) {
          let detail = res.statusText
          try {
            const body = await res.json()
            detail = body?.detail ?? detail
          } catch { /* not JSON */ }
          setState({ kind: 'error', message: detail })
          return
        }
        const snap: ShareSnapshot = await res.json()
        setState({ kind: 'ok', snapshot: snap })
      })
      .catch((err) => {
        if (cancelled) return
        setState({ kind: 'error', message: err instanceof Error ? err.message : 'Network error' })
      })
    return () => { cancelled = true }
  }, [token])

  return (
    <TooltipProvider>
      <div className="h-screen w-full flex flex-col bg-muted/30">
        {/* Top bar */}
        <header className="sticky top-0 z-10 h-12 flex items-center justify-between border-b border-border bg-white px-4 sm:px-6 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <a
              href={LANDING_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="text-sm font-semibold text-pharma-primary"
            >
              pharmatext
            </a>
            {state.kind === 'ok' && (
              <span className="text-xs text-muted-foreground truncate">
                Shared by{' '}
                <span className="text-foreground">
                  {state.snapshot.sender_name || 'Anonymous'}
                </span>
                {state.snapshot.sender_org && (
                  <>
                    {' · '}
                    <span className="text-foreground">{state.snapshot.sender_org}</span>
                  </>
                )}
              </span>
            )}
          </div>
          <a
            href={LANDING_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="shrink-0"
          >
            <Button
              size="sm"
              className="bg-pharma-primary hover:bg-pharma-primary/90 text-white text-[13px] gap-2"
            >
              Try pharmatext
              <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
            </Button>
          </a>
        </header>

        {state.kind === 'loading' && (
          <div className="flex-1 flex justify-center items-start py-24">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {state.kind === 'notfound' && (
          <CenteredGate>
            <ShareGateMessage
              title="Share link not found"
              body="This link is no longer valid. Ask the sender to send you a new one."
            />
          </CenteredGate>
        )}

        {state.kind === 'expired' && (
          <CenteredGate>
            <ShareGateMessage
              title="This share link has expired"
              body="Pharmatext share links last 7 days. Ask the sender to publish a new one."
            />
          </CenteredGate>
        )}

        {state.kind === 'error' && (
          <CenteredGate>
            <ShareGateMessage title="Something went wrong" body={state.message} />
          </CenteredGate>
        )}

        {state.kind === 'ok' && <SharedDraftBody snapshot={state.snapshot} />}
      </div>
    </TooltipProvider>
  )
}

function CenteredGate({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex-1 mx-auto w-full max-w-[840px] px-4 sm:px-6 py-8">
      {children}
    </main>
  )
}

function ShareGateMessage({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg border border-border bg-white p-8 mt-12 text-center">
      <h1 className="text-base font-semibold text-foreground">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
      <a
        href={LANDING_URL}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-block mt-4"
      >
        <Button size="sm" className="bg-pharma-primary hover:bg-pharma-primary/90 text-white">
          Learn about pharmatext
        </Button>
      </a>
    </div>
  )
}

function SharedDraftBody({ snapshot }: { snapshot: ShareSnapshot }) {
  const [surgeryClaimIndex, setSurgeryClaimIndex] = useState<number | null>(null)

  // Compute global indices (claim_id-aware, review_state-aware) once.
  const indices = useMemo(() => buildSnapshotIndices(snapshot), [snapshot])

  const sourceById = useMemo(() => {
    const m = new Map<string, ShareSnapshot['sources'][number]>()
    for (const s of snapshot.sources) m.set(s.id, s)
    return m
  }, [snapshot.sources])

  // Map each claim's DB UUID to its array index — citation clicks in the
  // doc come with a claim_id and need to land on the matching Surgery
  // panel entry.
  const claimIdToIndex = useMemo(() => {
    const m = new Map<string, number>()
    snapshot.claims.forEach((c, i) => { if (c.id) m.set(c.id, i) })
    return m
  }, [snapshot.claims])

  // Adapt ShareSnapshotClaim → AtomicBreakdownEntry so the in-app
  // Triage/Surgery primitives can render unchanged. The snapshot
  // schema already exposes every field the in-app shape needs (claim,
  // reasoning, suggested_edit, scrutiny_flags, evidences).
  const entries = useMemo<AtomicBreakdownEntry[]>(() => snapshot.claims.map((c) => ({
    claim: c.claim ?? c.resolved_claim ?? c.original_sentence ?? '',
    status: (c.status ?? 'unverified') as AtomicBreakdownEntry['status'],
    reasoning: c.reasoning ?? undefined,
    suggested_edit: c.suggested_edit ?? null,
    evidences: (c.evidences ?? []) as Evidence[],
    scrutiny_flags: (c.scrutiny_flags ?? []) as ScrutinyFlag[],
    review_state: c.review_state,
    original_sentence: c.original_sentence ?? undefined,
  })), [snapshot.claims])

  function handleCitationClick(claimId: string) {
    const idx = claimIdToIndex.get(claimId)
    if (idx !== undefined) setSurgeryClaimIndex(idx)
  }

  return (
    <div className="flex-1 min-h-0 flex overflow-hidden">
      {/* Left: editor column (mirrors Workbench.tsx structure) */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header row (h-12) — read-only title only, no action buttons. */}
        <div className="h-12 flex items-center px-4 border-b border-border shrink-0">
          <div className="text-sm font-semibold text-foreground truncate">
            {snapshot.draft.title || 'Untitled document'}
          </div>
        </div>

        {/* Editor area */}
        <div className="flex-1 overflow-y-auto bg-muted/30 px-8 py-8">
          <div className="relative mx-auto max-w-[800px] bg-white shadow-sm rounded-lg p-12 min-h-[600px]">
            <ShareDocument
              doc={snapshot.draft.content}
              indices={indices}
              onCitationClick={handleCitationClick}
            />

            {/* Inline abbreviations + bibliography footer — mirrors
                Workbench.tsx so the share view's bottom-of-card matches
                what the sender sees in the app. */}
            <InlineFooter snapshot={snapshot} indices={indices} sourceById={sourceById} />
          </div>

          {/* Footer attribution — pinned below the document card. */}
          <footer className="mt-8 pb-8 text-center">
            <p className="text-xs text-muted-foreground">
              Created with{' '}
              <a
                href={LANDING_URL}
                target="_blank"
                rel="noreferrer noopener"
                className="text-pharma-primary font-medium hover:underline"
              >
                pharmatext
              </a>{' '}
              — Pre-MLR compliance.
            </p>
          </footer>
        </div>
      </div>

      {/* Right: Triage / Surgery panel (320px). */}
      <ShareRightPanel
        entries={entries}
        sourceById={sourceById}
        surgeryClaimIndex={surgeryClaimIndex}
        onSelectClaim={setSurgeryClaimIndex}
      />
    </div>
  )
}

function InlineFooter({
  snapshot,
  indices,
  sourceById,
}: {
  snapshot: ShareSnapshot
  indices: ReturnType<typeof buildSnapshotIndices>
  sourceById: Map<string, ShareSnapshot['sources'][number]>
}) {
  const abbreviations = snapshot.draft.abbreviations ?? []
  const orderedSourceIds = indices.orderedSourceIds

  if (abbreviations.length === 0 && orderedSourceIds.length === 0) return null

  return (
    <div className="mt-4 border-t pt-3 space-y-1">
      {abbreviations.length > 0 && (
        <p className="text-xs text-gray-400 leading-relaxed">
          {abbreviations.map((a) => `${a.abbr}: ${a.full}`).join('; ')}
        </p>
      )}
      {orderedSourceIds.length > 0 && (
        <p className="text-xs text-gray-400 leading-relaxed">
          {orderedSourceIds.map((sid, i) => {
            const src = sourceById.get(sid)
            const text = src?.short_citation || src?.ama_citation || src?.filename || sid
            return (
              <span key={sid}>
                {i > 0 && '  '}
                {i + 1}. {renderMarkdownInline(text)}
              </span>
            )
          })}
        </p>
      )}
    </div>
  )
}
